// Pipeline Opportunity Types

export type OutreachStatus = 
  | 'Not Started'
  | 'Researching'
  | 'Outreach Sent'
  | 'Responded'
  | 'Meeting Scheduled'
  | 'Closed Lost';

export type RevenueRegularity = 'Monthly' | 'One-Time' | 'Annual';

// Matches pipeline_board view columns
export interface PipelineOpportunity {
  id: string;
  name: string;
  description: string | null;
  deal_value: number | null;
  monthly_value: number | null;
  commitment_months: number | null;
  probability: number | null;
  weighted_value: number | null;
  created_date: string;
  expected_close_date: string | null;
  source: string | null;
  next_action: string | null;
  next_action_date: string | null;
  notes: string | null;
  is_active: boolean;
  
  // Company & contact fields
  revenue_regularity: RevenueRegularity | null;
  company_domain: string | null;
  main_contact_name: string | null;
  main_contact_email: string | null;
  main_contact_linkedin: string | null;
  opp_summary: string | null;
  contact_classification: string | null;
  single_sales_objective: string | null;
  
  // Outreach & assessment fields
  interest_level: string | null;
  outreach_status: string | null;
  opportunity_assessment: OpportunityAssessment | null;
  tags: string[] | null;
  close_reason: string | null;
  competitor_lost_to: string | null;
  actual_close_date: string | null;
  
  // Stage fields
  stage_id: string;
  stage_name: string;
  stage_order: number;
  stage_color: string;
  stage_probability: number | null;
  stage_description: string | null;
  is_won: boolean;
  is_lost: boolean;
  
  // Product fields
  product_id: string | null;
  product_name: string | null;
  product_category: string | null;
  product_base_price: number | null;
  
  // Owner fields
  owner_id: string | null;
  owner_name: string | null;
  owner_email: string | null;
  owner_title: string | null;
  
  // Account fields (via account_id FK)
  account_id: string | null;
  account_name: string | null;
  account_domain: string | null;
  account_category: string | null;
  
  // Activity stats
  activity_count: number;
  last_activity_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface OpportunityAssessment {
  strengths: string[];
  risks: { text: string; severity: 'high' | 'medium' | 'low' }[];
}

export interface OpportunityContact {
  id: string;
  opportunity_id: string;
  contact_id: string | null;
  contact_name: string;
  contact_title: string | null;
  contact_email: string | null;
  buying_role: 'economic' | 'user' | 'technical' | 'coach' | 'champion' | 'unknown' | null;
  relationship_strength: 'cold' | 'warm' | 'strong' | 'champion' | null;
  notes: string | null;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

// Legacy outreach statuses (kept for backwards compatibility)
export const OUTREACH_STATUSES: { value: OutreachStatus; label: string; color: string }[] = [
  { value: 'Not Started', label: 'Not Started', color: '#6B7280' },
  { value: 'Researching', label: 'Researching', color: '#8B5CF6' },
  { value: 'Outreach Sent', label: 'Outreach Sent', color: '#3B82F6' },
  { value: 'Responded', label: 'Responded', color: '#10B981' },
  { value: 'Meeting Scheduled', label: 'Meeting Scheduled', color: '#F59E0B' },
  { value: 'Closed Lost', label: 'Closed Lost', color: '#EF4444' },
];

export const REVENUE_REGULARITIES: { value: RevenueRegularity; label: string; icon: string }[] = [
  { value: 'Monthly', label: 'Monthly', icon: '📅' },
  { value: 'One-Time', label: 'One-Time', icon: '1️⃣' },
  { value: 'Annual', label: 'Annual', icon: '📆' },
];

// Pipeline stages — Miller Heiman Strategic Selling model
export const PIPELINE_STAGES = [
  { id: 'Strategic Foundation', probability: 20, color: '#60a5fa', description: 'Blue Sheet live. Mapping all buying influences, identifying Coach, defining Single Sales Objective.' },
  { id: 'Stakeholder Alignment', probability: 40, color: '#06b6d4', description: 'Building individual relationships and win results per stakeholder. Green Sheets per call.' },
  { id: 'Solution Development', probability: 60, color: '#a78bfa', description: 'Translating offering into account language. Solution scoring against criteria.' },
  { id: 'Proposal & Navigation', probability: 75, color: '#f59e0b', description: 'Formal proposal out. Navigating Economic Buyer, Technical Buyer objections, internal politics.' },
  { id: 'Best Few / Close', probability: 90, color: '#84cc16', description: 'All red flags resolved. Close mechanics — contract, budget release, timeline.' },
  { id: 'Closed Won', probability: 100, color: '#22c55e', description: 'Deal closed, initial payment received and contracts signed.' },
  { id: 'Closed Lost', probability: 0, color: '#ef4444', description: 'Opportunity lost — did not advance or not a fit.' },
];

// Legacy - kept for backwards compatibility
export const PIPELINE_COLUMNS = OUTREACH_STATUSES.map(s => ({
  id: s.value,
  name: s.label,
  color: s.color,
}));
